import { Component } from '@theme/component';

/**
 * <ms-faq-component> — Acordeón exclusivo (single-open) para sections/ms-faq.liquid.
 *
 * Mejora progresiva sobre details/summary NATIVO:
 *   - Sin este JS, cada fila es un <details> independiente (abre/cierra solo, sin
 *     dependencias). La navegación y el contenido funcionan sin JavaScript.
 *   - Con este JS y data-single-open="true": al abrir una fila se cierran las demás,
 *     y se sincroniza aria-expanded del <summary> con el estado del <details>.
 *   - Teclado: Home/End mueven el foco al primer/último summary (Flecha arriba/abajo
 *     entre filas adyacentes). Enter/Espacio los maneja <summary> de forma nativa.
 *
 * Markup esperado (lo provee sections/ms-faq.liquid):
 *   <ms-faq-component data-single-open="true">
 *     <div class="ms-faq__list" role="region">
 *       <details class="ms-faq__item" ref="items[]">
 *         <summary ref="summaries[]" aria-controls="..panel.."
 *                  aria-expanded="false"
 *                  on:click="/onSummaryClick" on:keydown="/onKeydown">...</summary>
 *         <div class="ms-faq__panel" role="region">...</div>
 *       </details>
 *       ...
 *     </div>
 *   </ms-faq-component>
 *
 * @extends {Component<{ items: HTMLDetailsElement[], summaries: HTMLElement[] }>}
 */
class MsFaq extends Component {
  requiredRefs = ['summaries'];

  connectedCallback() {
    super.connectedCallback();

    // Sincronizar aria-expanded con el estado real al montar (por si "abrir la
    // primera" dejó una fila abierta) y, en modo single-open, garantizar que solo
    // una quede abierta.
    if (this.#singleOpen) {
      const open = this.#items.filter((item) => item.open);
      // Conservar la primera abierta; cerrar el resto.
      open.slice(1).forEach((item) => {
        item.open = false;
      });
    }
    this.#syncAria();
  }

  /** @returns {boolean} */
  get #singleOpen() {
    return this.dataset.singleOpen === 'true';
  }

  /** @returns {HTMLDetailsElement[]} */
  get #items() {
    const { items } = this.refs;
    return Array.isArray(items) ? items : items ? [items] : [];
  }

  /** @returns {HTMLElement[]} */
  get #summaries() {
    const { summaries } = this.refs;
    return Array.isArray(summaries) ? summaries : summaries ? [summaries] : [];
  }

  /**
   * Click sobre un <summary>. El toggle del <details> es nativo; aquí solo, en
   * modo single-open, cerramos las demás filas tras abrir esta.
   * @param {Event} event
   */
  onSummaryClick(event) {
    const summary = /** @type {HTMLElement} */ (event.target)?.closest('summary');
    if (!summary) return;

    const details = /** @type {HTMLDetailsElement | null} */ (summary.closest('details'));
    if (!details) return;

    if (this.#singleOpen) {
      // `details.open` aún refleja el estado PREVIO durante el click; tras abrirla
      // (toggle nativo) será true. Cerramos las demás en el próximo frame para no
      // pelear con el toggle por defecto del navegador.
      requestAnimationFrame(() => {
        if (details.open) {
          this.#items.forEach((item) => {
            if (item !== details) item.open = false;
          });
        }
        this.#syncAria();
      });
    } else {
      requestAnimationFrame(() => this.#syncAria());
    }
  }

  /**
   * Teclado sobre los summaries: Home/End al primero/último; Flechas entre filas.
   * Enter/Espacio se delegan al comportamiento nativo de <summary>.
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    const summaries = this.#summaries;
    const count = summaries.length;
    if (count === 0) return;

    const current = summaries.indexOf(
      /** @type {HTMLElement} */ (event.target)?.closest('summary')
    );
    if (current < 0) return;

    let next = -1;
    switch (event.key) {
      case 'ArrowDown':
        next = (current + 1) % count;
        break;
      case 'ArrowUp':
        next = (current - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    summaries[next]?.focus();
  }

  /** Sincroniza aria-expanded de cada summary con el estado open de su details. */
  #syncAria() {
    this.#items.forEach((item) => {
      const summary = item.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');
    });
  }
}

if (!customElements.get('ms-faq-component')) {
  customElements.define('ms-faq-component', MsFaq);
}
